import moment from 'moment';

const UNKNOWN_EXPIRY_DATE_KEY = '__unknown_expiry_date__';
const UNKNOWN_EXPIRY_DATE_LABEL = 'Unknown';

const getExpiryDateKey = (value) => {
	if (!value) {
		return UNKNOWN_EXPIRY_DATE_KEY;
	}

	const date = moment(value);
	if (!date.isValid()) {
		return UNKNOWN_EXPIRY_DATE_KEY;
	}

	return date.startOf('day').format('YYYY-MM-DD');
};

const formatExpiryDateKeyToLabel = (dateKey) => {
	if (!dateKey || dateKey === UNKNOWN_EXPIRY_DATE_KEY) {
		return UNKNOWN_EXPIRY_DATE_LABEL;
	}

	return moment(dateKey, 'YYYY-MM-DD', true).format('MMM D');
};

/**
 * Format expiry dates for display in pick list
 * @param {Object} expiryDates - Object with expiry dates keyed by position (1, 2, 3, etc.)
 * @param {number} quantity - Current quantity of products
 * @param {boolean} isMarket - Whether this is a market machine
 * @returns {Array} Array of grouped expiry date strings in "count x date" format
 */
export const formatExpiryDatesForDisplay = (expiryDates, quantity, isMarket) => {
	const normalizedQuantity = Math.max(0, Math.round(Number(quantity) || 0));
	if (!normalizedQuantity) {
		return [];
	}

	const quantityToDisplay = isMarket ? 1 : normalizedQuantity;
	const quantitiesByDateKey = {};

	for (let i = 1; i <= quantityToDisplay; i += 1) {
		const dateKey = getExpiryDateKey(expiryDates?.[i]);
		quantitiesByDateKey[dateKey] = (quantitiesByDateKey[dateKey] || 0) + 1;
	}

	const sortedDateKeys = Object.keys(quantitiesByDateKey).sort((left, right) => {
		const leftUnknown = left === UNKNOWN_EXPIRY_DATE_KEY;
		const rightUnknown = right === UNKNOWN_EXPIRY_DATE_KEY;

		if (leftUnknown && rightUnknown) {
			return 0;
		}
		if (leftUnknown) {
			return 1;
		}
		if (rightUnknown) {
			return -1;
		}

		return left.localeCompare(right);
	});

	return sortedDateKeys.map(dateKey => {
		const quantityForDate = quantitiesByDateKey[dateKey] || 0;
		return `${quantityForDate} x ${formatExpiryDateKeyToLabel(dateKey)}`;
	});
};

/**
 * Build a compact grouped expiry date summary for list rows.
 * @param {Array} groupedExpiryDates - Output of formatExpiryDatesForDisplay
 * @param {number} maxGroups - Maximum groups to display before truncating
 * @returns {string}
 */
export const formatCompactExpiryDatesForDisplay = (groupedExpiryDates = [], maxGroups = 2) => {
	if (!groupedExpiryDates.length) {
		return '';
	}

	const normalizedMaxGroups = Math.max(1, Math.round(Number(maxGroups) || 0));
	const visibleGroups = groupedExpiryDates.slice(0, normalizedMaxGroups);
	const remainingGroups = Math.max(0, groupedExpiryDates.length - visibleGroups.length);

	if (!remainingGroups) {
		return visibleGroups.join(', ');
	}

	return `${visibleGroups.join(', ')} +${remainingGroups} more`;
};

/**
 * Get expiry date warnings for pick list display
 * @param {Object} expiryDates - Object with expiry dates keyed by position
 * @param {number} quantity - Current quantity of products
 * @param {Object} tenantSettings - Tenant settings containing expiry date thresholds
 * @returns {Object} Object containing warning information
 */
export const getExpiryDateWarningsForPicklist = (expiryDates, quantity, tenantSettings) => {
	if (!quantity || !tenantSettings?.useExpiryDates) {
		return { hasWarnings: false, warningText: null };
	}

	const expiryDatesObj = expiryDates || {};
	const currentDate = moment().startOf('day');
	let expiredCount = 0;
	let expiringSoonCount = 0;
	let expiringTodayCount = 0;
	let unknownCount = 0;

	// Check each expiry date
	for (let i = 1; i <= quantity; i++) {
		const date = expiryDatesObj[i];
		if (date) {
			const daysDiff = moment(date).startOf('day').diff(currentDate, 'days');

			if (daysDiff < 0) {
				expiredCount++;
			} else if (daysDiff === 0) {
				expiringTodayCount++;
			} else if (daysDiff <= (tenantSettings.expiryDateThresholds?.[0] || 7)) {
				expiringSoonCount++;
			}
		}
		else {
			unknownCount++;
		}
	}

	const warnings = [];

	if (expiredCount > 0) {
		warnings.push(`${expiredCount} expired`);
	}

	if (expiringTodayCount > 0) {
		warnings.push(`${expiringTodayCount} expire today`);
	}

	if (expiringSoonCount > 0) {
		warnings.push(`${expiringSoonCount} expire soon`);
	}
	if (unknownCount > 0) {
		warnings.push(`${unknownCount} unknown expiry`);
	}

	return {
		hasWarnings: warnings.length > 0,
		warningText: warnings.join(', '),
		isUrgent: expiredCount > 0 || expiringTodayCount > 0,
	};
};
